import React, { type ChangeEvent, type ReactElement, useCallback, useEffect, useState } from 'react'
import {
  CircularProgress,
  IconButton as MuiIconButton,
  InputAdornment,
  Skeleton,
  TablePagination,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import tendersBackground from 'assets/tendersBackground.svg'
import { ReactComponent as RightArrowIcon } from 'assets/rightArrowIcon.svg'
import { ReactComponent as ClearIcon } from 'assets/clearIcon.svg'
import { ReactComponent as SearchIcon } from 'assets/search.svg'
import { ReactComponent as DownloadIcon } from 'assets/downloadIcon.svg'
import { StoreStatus, Role } from 'common/utils/types'
import IconButton from 'common/ui/icon-button'
import { BUTTON_SIZE, BUTTON_VARIANTS } from 'common/ui/theme-button/ThemeButton'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { debounce } from 'lodash'
import { VendorAIStatus } from 'features/vendor/vendor.types'
import ThemeButton from 'common/ui/theme-button'
import { type GeneratedFile, GeneratedFileType } from 'common/files/files.types'
import ThemeConfirmDialog from 'common/ui/theme-confirm-dialog'
import { downloadFile, getFileUrl } from 'common/files/files.service'
import { getDateRetryVerification } from 'utils/functions'

import { getAIResults, getListOfAIVendors, respondToAIVerification } from '../../vendorList.service'
import { setAIVendorSearch } from '../../vendorList.slice'
import styles from '../vendor-list/VendorList.module.scss'
import { VENDORS_PER_PAGE, vendorStatusColors, vendorStatusMessage } from '../../vendorListFunctions'
import TooltipCellWrapper from '../components/tooltipCellWrapper/TooltipCellWrapper'
import CellWrapper from '../components/cellWrapper/CellWrapper'
import { type ListAIVendor } from '../../vendorList.types'
import DeclineVerificationDialog from '../../../vendor/verification-profile/decline-verification-dialog'

interface VendorListProps {
  openVendorProfile: (vendor: ListAIVendor) => void
  showReasonPopup: (vendor: ListAIVendor) => void
}

const AIVerification: React.FC<VendorListProps> = ({ openVendorProfile, showReasonPopup }): ReactElement => {
  const { palette } = useTheme()
  const navigate = useNavigate()

  const [openAcceptDialog, setOpenAcceptDialog] = useState<boolean>(false)
  const [openRejectDialog, setOpenRejectDialog] = useState<boolean>(false)
  const [vendorId, setVendorId] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [loadingVendorId, setLoadingVendorId] = useState<string>('')

  const dispatch = useAppDispatch()

  const { aiVendorList, aiVendorCount, aiVendorStatus, aiVendorSearch, aiVendorPage } = useAppSelector(
    (state) => state.vendorList,
  )

  useEffect(() => {
    loadVendors(0, aiVendorSearch)
  }, [])

  useEffect(() => {
    if (loadingVendorId) {
      setLoading(true)
    } else {
      setLoading(false)
    }
  }, [loadingVendorId])

  const handleDownloadClick = async (aiRefDocs: GeneratedFile[], vendorId: string): Promise<void> => {
    setLoadingVendorId(vendorId)

    try {
      for (const file of aiRefDocs) {
        try {
          const generatedFile = await getFileUrl({
            key: file.key,
            mode: 'r',
          })
          await downloadFile(GeneratedFileType.AIVendorRefDoc, generatedFile)
        } catch (e) {
          console.error('error downloading file: ', e)
        }
      }
    } catch (e) {
      console.error('error downloading file: ', e)
    } finally {
      setLoading(false)
    }
  }

  const loadVendors = (page: number, search: string): void => {
    const payload = {
      ipp: VENDORS_PER_PAGE,
      page,
      search,
    }

    void dispatch(getListOfAIVendors(payload))
  }

  const debouncedLoadVendors = useCallback(
    debounce((page: number, searchString: string) => {
      loadVendors(page, searchString)
    }, 500),
    [],
  )

  const setSearchString = (search: string): void => {
    dispatch(setAIVendorSearch(search))
    debouncedLoadVendors(0, search)
  }

  const handleCloseAcceptDialog = (): void => {
    setOpenAcceptDialog(false)
  }

  const handleCloseVerificationDialog = (): void => {
    setOpenRejectDialog(false)
    loadVendors(0, aiVendorSearch)
  }

  const handleVerifyVendor = (): void => {
    if (vendorId) {
      void dispatch(respondToAIVerification({ vendorId, aiStatus: VendorAIStatus.approved }))
    }
    setOpenAcceptDialog(false)
  }

  const handleRejectVendor = (reason: string, months?: string): void => {
    if (vendorId) {
      void dispatch(
        respondToAIVerification({
          vendorId,
          aiStatus: VendorAIStatus.rejected,
          aiVerificationNote: reason,
          aiVerificationRetryDate: getDateRetryVerification(months),
        }),
      )
      loadVendors(0, aiVendorSearch)
    }
    setOpenRejectDialog(false)
  }

  const handlePageChange = (event: React.MouseEvent | null, page: number): void => {
    loadVendors(page, aiVendorSearch)
  }

  return (
    <div className={styles.container}>
      <div
        className={styles.backgroundImage}
        style={{
          backgroundImage: `url(${tendersBackground})`,
        }}
      />
      <div className={styles.searchContainer}>
        <TextField
          variant="outlined"
          placeholder="Pretraži"
          value={aiVendorSearch}
          onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setSearchString(e.target.value)
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: '14px',
              color: palette.blue100.main,
              height: '70px',
            },
            '&.MuiTextField-root': {
              width: '100%',
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <MuiIconButton
                  onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault()
                  }}
                  onClick={() => {
                    setSearchString(aiVendorSearch ? '' : aiVendorSearch)
                  }}
                >
                  {aiVendorSearch ? <ClearIcon /> : <SearchIcon />}
                </MuiIconButton>
              </InputAdornment>
            ),
          }}
        />
      </div>
      <div id="vendorListWrapper" className={styles.vendorListWrapper}>
        {aiVendorStatus === StoreStatus.Succeeded && !aiVendorList?.length && (
          <div className={styles.noResults}>Nema rezulata za trenutnu pretragu</div>
        )}
        {aiVendorStatus !== StoreStatus.Loading && aiVendorStatus !== StoreStatus.Idle
          ? aiVendorList?.map((vendor) => (
              <div key={vendor.id} className={styles.vendorItem}>
                <TooltipCellWrapper
                  title="Naziv vendora"
                  value={
                    vendor?.user?.role === Role.consultant
                      ? vendor?.user
                        ? `${vendor.user.name?.first ?? ''} ${vendor.user.name?.last ?? ''}`
                        : '-'
                      : vendor?.name ?? '-'
                  }
                  width="16%"
                  style={{ minWidth: '130px', maxWidth: '130px' }}
                />
                <div className={styles.divider} />
                <CellWrapper title="PIB" value={vendor.tin} style={{ minWidth: '82px', maxWidth: '82px' }} />
                <div className={styles.divider} />
                <CellWrapper
                  title="E-mail"
                  value={vendor.user?.email}
                  width="20%"
                  style={{ minWidth: '230px', maxWidth: '230px' }}
                />
                <div className={styles.divider} />
                <CellWrapper
                  title="AI Status"
                  value={vendorStatusMessage(vendor.aiStatus)}
                  valueSx={{ color: vendorStatusColors(vendor.aiStatus, palette) }}
                  style={{ minWidth: '90px', maxWidth: '90px' }}
                />
                <div className={styles.divider} />
                <CellWrapper
                  title="AI upitnik rezultat"
                  value={
                    <div className={styles.downloadContainer}>
                      <ThemeButton
                        onClick={() => {
                          void dispatch(getAIResults(vendor.id))
                          navigate(`/lista/${vendor.id}/rezultati`)
                        }}
                        size={BUTTON_SIZE.small}
                        sx={{
                          backgroundColor: palette.charts.green200.main,
                          '&:hover': {
                            backgroundColor: palette.charts.green200.main,
                          },
                          maxHeight: '22px',
                          padding: '0 0',
                        }}
                      >
                        Vidi rezultate
                      </ThemeButton>
                    </div>
                  }
                  width="16%"
                  style={{ minWidth: '150px', maxWidth: '150px' }}
                />
                <div className={styles.divider} />
                <CellWrapper
                  title="Dokumenti"
                  value={
                    vendor?.aiReferenceDocuments && vendor?.aiReferenceDocuments?.documents?.length > 0 ? (
                      loading && vendor?.id === loadingVendorId ? (
                        <div className={styles.downloadContainer}>
                          <CircularProgress size={20} color="primary" />
                          <Typography variant="title3">Skini dokumente</Typography>
                        </div>
                      ) : (
                        <div className={styles.downloadContainer}>
                          <DownloadIcon
                            height={20}
                            width={20}
                            onClick={() => {
                              void handleDownloadClick(vendor.aiReferenceDocuments.documents, vendor.id)
                            }}
                            style={{ cursor: 'pointer' }}
                          />
                          <Typography variant="title3">Skini dokumente</Typography>
                        </div>
                      )
                    ) : (
                      <div className={styles.docNotExist}>
                        <Typography variant="title3" sx={{ color: palette.gray.main }}>
                          Nema dokumenata
                        </Typography>
                      </div>
                    )
                  }
                  style={{ minWidth: '150px', maxWidth: '150px' }}
                />
                <div className={styles.divider} />
                {vendor.aiStatus === VendorAIStatus.approved && (
                  <div className={styles.buttons} style={{ maxWidth: '25%', minWidth: '145px' }}>
                    <div>Vidi profil</div>
                    <IconButton
                      variant={BUTTON_VARIANTS.outlined}
                      size={BUTTON_SIZE.small}
                      sx={{
                        borderColor: palette.blue600.main,
                        backgroundColor: palette.blue600.main,
                        width: '24px',
                        minWidth: '24px',
                        height: '24px',
                        padding: 0,
                      }}
                      onClick={() => {
                        openVendorProfile(vendor)
                      }}
                      icon={<RightArrowIcon style={{ stroke: 'white' }} />}
                    />
                  </div>
                )}
                {vendor.aiStatus === VendorAIStatus.rejected && (
                  <div className={styles.buttons} style={{ maxWidth: '25%', minWidth: '145px' }}>
                    <div>Vidi razlog odbijanja</div>
                    <IconButton
                      variant={BUTTON_VARIANTS.outlined}
                      size={BUTTON_SIZE.small}
                      sx={{
                        borderColor: palette.blue600.main,
                        backgroundColor: palette.blue600.main,
                        width: '24px',
                        minWidth: '24px',
                        height: '24px',
                        padding: 0,
                      }}
                      onClick={() => {
                        showReasonPopup(vendor)
                      }}
                      icon={<RightArrowIcon style={{ stroke: 'white' }} />}
                    />
                  </div>
                )}
                {vendor.aiStatus === VendorAIStatus.pending && (
                  <div className={styles.buttonWrapper} style={{ maxWidth: '25%' }}>
                    <ThemeButton
                      onClick={() => {
                        setVendorId(vendor.id)
                        setOpenAcceptDialog(true)
                      }}
                      sx={{
                        backgroundColor: palette.charts.green200.main,
                        '&:hover': {
                          backgroundColor: palette.charts.green200.main,
                        },
                        minWidth: '145px',
                        maxWidth: '145px',
                        padding: '0 0',
                      }}
                    >
                      VERIFIKUJ VENDORA
                    </ThemeButton>
                    <ThemeButton
                      onClick={() => {
                        setVendorId(vendor.id)
                        setOpenRejectDialog(true)
                      }}
                      sx={{
                        backgroundColor: palette.charts.indigo200.main,
                        '&:hover': {
                          backgroundColor: palette.charts.indigo200.main,
                        },
                        minWidth: '145px',
                        maxWidth: '145px',
                        padding: '0 0',
                      }}
                    >
                      ODBIJ VENDORA
                    </ThemeButton>
                  </div>
                )}
              </div>
            ))
          : Array(VENDORS_PER_PAGE)
              .fill(0)
              .map((value, index) => (
                <Skeleton
                  key={`skeleton-${index}`}
                  variant="rounded"
                  width={'100%'}
                  height={96}
                  sx={{ backgroundColor: palette.blue700.main, marginRight: '1rem' }}
                />
              ))}
        <div className={styles.paginationWrapper}>
          {aiVendorList?.length > 0 && (
            <TablePagination
              count={aiVendorCount}
              onPageChange={handlePageChange}
              page={aiVendorPage}
              rowsPerPage={VENDORS_PER_PAGE}
              rowsPerPageOptions={[VENDORS_PER_PAGE]}
              component="div"
            />
          )}
        </div>
        {openAcceptDialog && (
          <ThemeConfirmDialog
            onClose={handleCloseAcceptDialog}
            open={openAcceptDialog}
            title={'Verifikuj vendora?'}
            confirmButtonText={'VERIFIKUJ'}
            confirmButtonAction={handleVerifyVendor}
            declineButtonText={'IZAĐI'}
            description={'Pritiskom na dugme “Verifikuj” ovaj korisnik postaje AI vendor.'}
          />
        )}
        {openRejectDialog && (
          <DeclineVerificationDialog
            onClose={handleCloseVerificationDialog}
            confirmButtonText={'ODBIJ'}
            declineButtonText={'IZAĐI'}
            title="Odbij zahtev za ai verifikaciju?"
            subtitle="Ukoliko želite da odbijete ovog vendora morate napisati razlog odbijanja i naznačiti datum kada vendor može ponovo da aplicira za verifikaciju."
            textInputName="Razlog odbijanja"
            displayMonths={true}
            open={openRejectDialog}
            confirmButtonAction={handleRejectVendor}
          />
        )}
      </div>
    </div>
  )
}

export default AIVerification
